import React, { useEffect } from 'react';
import { useField } from 'formik';
import { ItemType as RadiobuttonItem } from '@service-victoria/component-library/components/RadioButton';

import Text from '@service-victoria/component-library/components/Text';
import Input from '@service-victoria/component-library/components/Input';
import RadioButton from '@service-victoria/component-library/components/RadioButton';
import Checkbox from '@service-victoria/component-library/components/Checkbox';
import Dropdown from '@service-victoria/component-library/components/Dropdown';
import DatePicker from 'Components/DatePicker';
import * as Styled from '../../styled';
import { ListOfStates } from 'utils/constants';
import dayjs from 'dayjs';

const licencePath = 'assess.SupportingDocuments.driversLicence';

const driversLicenceCheckbox: RadiobuttonItem[] = [
  {
    label: 'I have an Australian driver licence',
    value: 'yes',
    inputProps: { 'data-testid': 'DriverLicenseYes-RadioButton' }
  },
  {
    label: "I don't have an Australian driver licence",
    value: 'no',
    inputProps: { 'data-testid': 'DriverLicenseNo-RadioButton' }
  }
];

const now = dayjs(new Date());
const youngestDriverLicence = now.subtract(15, 'years').format('YYYY-MM-DD');
const oldestDriverLicence = now.subtract(120, 'years').startOf('year').format('YYYY-MM-DD');

export const DriverLicenceSelector: React.FC<Record<string, never>> = () => {
  const [{ value: hasDriverLicence }] = useField<string>('assess.hasDriverLicence');

  useAutopopulateIdentity();

  return (
    <>
      <Text.Heading.H3 fontWeight={600} id='assess.hasDriverLicence.label'>
        Proof of residence
      </Text.Heading.H3>
      <Text.Body>We need to know where you live. The best proof is a driver licence.</Text.Body>
      <RadioButton
        groupLabel=''
        items={driversLicenceCheckbox}
        name='assess.hasDriverLicence'
        aria-labelledby='assess.hasDriverLicence.label'
      />
      {hasDriverLicence === 'yes' && (
        <>
          <Styled.Spacer16 />
          <Text.Heading.H3 fontWeight={600}>Drivers licence</Text.Heading.H3>
          <Styled.Spacer16 />
          <Dropdown
            label='State of issue'
            name={`${licencePath}.registrationState`}
            options={ListOfStates}
            selectProps={{
              'data-testid': 'DriverLicenseState-DropDown'
            }}
          />
          <Styled.Spacer16 />
          <Input
            label='Licence number'
            name={`${licencePath}.licenceNo`}
            data-testid='LicenseNumber-Input'
            errorLabelProps={{
              'data-testid': 'LicenseNumber-InputError'
            }}
          />
          <Styled.Spacer16 />
          <Input
            label='First name'
            name={`${licencePath}.givenNames`}
            data-testid='LicenseFirstName-Input'
            errorLabelProps={{
              'data-testid': 'LicenseFirstName-InputError'
            }}
          />
          <Styled.Spacer16 />
          <Input
            label='Family name'
            name={`${licencePath}.familyName`}
            data-testid='LicenseLastName-Input'
            errorLabelProps={{
              'data-testid': 'LicenseLastName-InputError'
            }}
          />
          <Styled.Spacer16 />
          <DatePicker
            name={`${licencePath}.dateOfBirth`}
            label='Date of birth'
            min={oldestDriverLicence}
            max={youngestDriverLicence}
            data-testid='LicenseDoB-DatePicker'
            errorLabelProps={{
              'data-testid': 'LicenseDoB-DatePickerError'
            }}
          />
          <Styled.Spacer16 />
          <Checkbox
            name={`${licencePath}.consent`}
            label='I consent to have my driver licence checked with the issuer or official record holder.'
            data-testid='DriverLicenseConsent-CheckBox'
            errorLabelProps={{
              'data-testid': 'DriverLicenseConsent-CheckBoxError'
            }}
          />
        </>
      )}
      {hasDriverLicence === 'no' && (
        <Text.Body>
          To approve your exemption, we need to know where you normally live. We&apos;ll email
          telling you how to get these details to the Victorian Department of Health.
        </Text.Body>
      )}
    </>
  );
};

function useAutopopulateIdentity() {
  const [, , { setValue: setGivenName }] = useField<string>(`${licencePath}.givenNames`);
  const [, , { setValue: setFamilyName }] = useField<string>(`${licencePath}.familyName`);
  const [{ value: givenName }] = useField<string>('identity.givenName');
  const [{ value: familyName }] = useField<string>('identity.familyName');

  useEffect(() => {
    setGivenName(givenName);
    setFamilyName(familyName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
